/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/** How much of a FIT file's framing to expect. */
public enum class DecodeMode {
    /** A complete file: header, records, CRC. Anything else is an error. */
    NORMAL,

    /** Read the header but do not hold it against the file if it is wrong. */
    SKIP_HEADER,

    /** Bare records, with no file header and no trailing CRC. */
    DATA_ONLY,
}

/**
 * Knobs on the decode.
 *
 * The defaults produce what most callers want: a fully expanded, profile-typed
 * view of the file.
 */
public data class DecodeOptions(
    val mode: DecodeMode = DecodeMode.NORMAL,

    /**
     * Unpack bit-packed fields into the fields they represent.
     *
     * With this off, `record.speed` stays absent on a file that only stores
     * `compressed_speed_distance`.
     */
    val expandComponents: Boolean = true,

    /**
     * Keep messages and fields the profile does not know.
     *
     * They decode into a [Mesg] named `unknown` with numbered fields, and land
     * in [FitMessages.unknownMesgs].
     */
    val includeUnknownData: Boolean = false,

    /**
     * Fold the finer-grained `hr` messages into the `record` messages they
     * overlap, so heart rate appears where the rest of the data is.
     */
    val mergeHeartRates: Boolean = true,
)

/**
 * Something the decoder could not make sense of.
 *
 * [FitDecoder.decode] collects these instead of throwing, so a truncated or
 * corrupt file still yields everything that decoded before the problem.
 */
public data class FitError(
    val message: String,
    val bytePosition: Int,
    val cause: Throwable? = null,
) {
    internal constructor(e: FitFormatException) : this(e.message ?: "decode error", e.bytePosition, e)

    override fun toString(): String = message
}

/** Everything a decode produced. */
public data class DecodeResult(
    /** Decoded messages, sorted by type. */
    val messages: FitMessages,

    /** The same messages, in the order they appear in the file. */
    val mesgs: List<Mesg>,

    /** Profile version from the first file header, if there was one. */
    val profileVersion: UShort?,

    /** Empty when the whole input decoded cleanly. */
    val errors: List<FitError>,
) {
    public val isSuccess: Boolean get() = errors.isEmpty()
}
